import React from 'react';
import {
  Box,
  Button,
  Card,
  CardContent,
  CardMedia,
  Chip,
  Stack,
  Typography,
  useTheme,
} from '@mui/material';
import ArrowForwardIcon from '@mui/icons-material/ArrowForward';
import { Link as RouterLink } from 'react-router-dom';
import { tokens } from '../../../theme';

const PRODUCT_DETAILS_BASE = '/item';

const getProductImage = (product) => {
  const image =
    product?.primaryImage ||
    product?.images?.find((item) => item.isPrimary) ||
    product?.images?.[0];

  return image?.url || '';
};

const getProductLinkId = (product) => {
  return product?.slug || product?._id || product?.id;
};

const ProductCard = ({ product }) => {
  const theme = useTheme();
  const colors = tokens(theme.palette.mode);

  const imageUrl = getProductImage(product);
  const linkId = getProductLinkId(product);

  return (
    <Card
      sx={{
        height: '100%',
        borderRadius: '24px',
        overflow: 'hidden',
        backgroundColor: 'rgba(255,255,255,0.06)',
        border: '1px solid rgba(255,255,255,0.10)',
        color: 'white',
        boxShadow: '0 24px 80px rgba(0,0,0,0.28)',
        transition: 'transform 160ms ease, border-color 160ms ease',
        '&:hover': {
          transform: 'translateY(-6px)',
          borderColor: colors.greenAccent[500],
        },
      }}
    >
      <Box
        sx={{
          height: 260,
          background:
            'radial-gradient(circle at top, rgba(0,255,170,0.12), rgba(255,255,255,0.03))',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
        }}
      >
        {imageUrl ? (
          <CardMedia
            component="img"
            image={imageUrl}
            alt={product.name}
            sx={{
              height: '100%',
              width: '100%',
              objectFit: 'cover',
            }}
          />
        ) : (
          <Typography color={colors.grey[400]} fontWeight="bold">
            USFuel
          </Typography>
        )}
      </Box>

      <CardContent sx={{ p: 3 }}>
        <Stack direction="row" spacing={1} mb={2} flexWrap="wrap">
          <Chip
            label={product.category}
            size="small"
            sx={{
              color: colors.greenAccent[300],
              backgroundColor: 'rgba(0,255,170,0.10)',
              fontWeight: 700,
            }}
          />

          {product.featured && (
            <Chip
              label="Featured"
              size="small"
              sx={{
                color: '#fff',
                backgroundColor: 'rgba(104,112,250,0.45)',
                fontWeight: 700,
              }}
            />
          )}
        </Stack>

        <Typography
          variant="h4"
          fontWeight="900"
          sx={{
            fontFamily: 'Poppins, sans-serif',
            mb: 1,
            letterSpacing: '-0.03em',
          }}
        >
          {product.name}
        </Typography>

        <Typography
          sx={{
            color: colors.grey[300],
            minHeight: '72px',
            lineHeight: 1.55,
            fontFamily: 'Poppins, sans-serif',
          }}
        >
          {product.shortDescription || product.tagline || product.description}
        </Typography>

        <Stack direction="row" spacing={1} mt={2} flexWrap="wrap">
          {(product.features || []).slice(0, 3).map((feature) => (
            <Chip
              key={feature.title}
              label={feature.title}
              size="small"
              variant="outlined"
              sx={{
                color: colors.grey[200],
                borderColor: 'rgba(255,255,255,0.16)',
                mb: 1,
              }}
            />
          ))}
        </Stack>

        <Box mt={3} display="flex" alignItems="center" justifyContent="space-between" gap={2}>
          <Box>
            <Typography variant="body2" color={colors.grey[400]}>
              Starting at
            </Typography>
            <Typography variant="h6" fontWeight="bold">
              {product.pricing?.displayPriceText || 'Contact for pricing'}
            </Typography>
          </Box>

          <Button
            component={RouterLink}
            to={`${PRODUCT_DETAILS_BASE}/${linkId}`}
            variant="contained"
            endIcon={<ArrowForwardIcon />}
            sx={{
              borderRadius: '999px',
              px: 2.5,
              color: '#06100c',
              backgroundColor: colors.greenAccent[400],
              fontWeight: 900,
              '&:hover': {
                backgroundColor: colors.greenAccent[300],
              },
            }}
          >
            View
          </Button>
        </Box>
      </CardContent>
    </Card>
  );
};

export default ProductCard;